// cSpell:ignore Afrekening downloaden Overzicht
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { StatementSnapshot } from "./scenes/SettlementInfo/SettlementForm/steps/buildStatementSnapshot";

// A settlement's statements are only ever downloaded by the office ("Overzicht
// downloaden"), which forwards them itself: Rentio sends none of them, not
// with a payment request, not to an owner (Lewis, 2026-10-20). The PDF is
// rendered by the API (serverless), and these are the calls the download
// makes.

// The signed URL of a rendered statement, or null while it is not rendered.
export const fetchStatementUrl = async (
  settlementId: string,
  variant: string,
): Promise<string | null> => {
  const response = await api.get<{ data: { url: string | null } }>(
    `/settlements/${settlementId}/pdf?variant=${encodeURIComponent(variant)}`,
    { isV2: true },
  );
  return response.data?.data?.url ?? null;
};

// Asks the API to render a statement and resolves to the variant to poll.
// For a confirmed settlement the API renders the statement it froze for that
// party, not the one sent here, with its number (which only the API knows),
// and names the key itself: poll that one. Its PDF may be there already. A
// statement the settlement froze is not sent along (`isFrozen`): the API
// reads it itself, and a big building's statements were sent twice
// (decision 16 of 2026-10-20).
export const requestStatementRender = async (params: {
  settlementId: string;
  variant: string;
  statement: StatementSnapshot;
  isFrozen?: boolean;
}): Promise<string> => {
  const { settlementId, variant, statement, isFrozen = false } = params;
  const response = await api.post<{ data?: { variant?: string } }>(
    `/settlements/${settlementId}/pdf`,
    {
      isV2: true,
      data: isFrozen ? { variant } : { variant, snapshot: statement },
    },
  );
  return response.data?.data?.variant ?? variant;
};

// Characters no file system accepts in a file name, control characters
// (C0, DEL and C1) included. Accents and harmless punctuation stay.
// eslint-disable-next-line no-control-regex
const INVALID_FILENAME_CHARACTERS = /[\\/:*?"<>|\u0000-\u001f\u007f-\u009f]+/g;
// Invisible characters that change how a name reads without showing up:
// the bidirectional controls (a right-to-left override can make a PDF's
// name read as an .exe), zero-width spaces and joiners, the soft hyphen,
// the byte order mark and the tag characters that hide text. A tenant's or
// settlement's name puts them in the file name, so they are dropped.
const INVISIBLE_FILENAME_CHARACTERS =
  /[\u00ad\u061c\u180e\u200b-\u200f\u202a-\u202e\u2060-\u206f\ufeff\u{e0000}-\u{e007f}]/gu;
const FALLBACK_FILENAME = "statement";

// A readable filename: "Afrekening 2026 - Lewis Steen.pdf", accents included.
export const statementFilename = (title: string): string =>
  `${
    title
      .replace(INVISIBLE_FILENAME_CHARACTERS, "")
      .replace(INVALID_FILENAME_CHARACTERS, "-")
      .replace(/\s+/g, " ")
      .trim() || FALLBACK_FILENAME
  }.pdf`;
